import React from 'react';
import { cn } from '../utils/lib';
import FallbackImage from '../assets/images/fallback.webp';

export default function Image({
  withPlaceholder = false,
  placeholder,
  src,
  alt = '',
  className,
  wrapperClassName,
  rootMargin = '100px',
  threshold = 0.1,
  fallback,
  ...props
}) {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const imgRef = React.useRef(null);
  const observerRef = React.useRef(null);

  // Main useEffect for lazy loading and error handling
  React.useEffect(() => {
    const imgElement = imgRef.current;

    if (!imgElement) return;

    // Handler for when the image successfully loads
    const handleLoad = () => {
      setIsLoaded(true);
      imgElement.removeEventListener('load', handleLoad);
    };

    const handleError = () => {
      setIsLoaded(false);
      // Use the fallback prop if provided, otherwise use a default fallback image
      imgElement.src = fallback || FallbackImage;
    };

    // Intersection Observer callback to handle lazy loading
    const observerCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          imgElement.src = imgElement.dataset.src;

          // Add event listeners for load and error events
          imgElement.addEventListener('load', handleLoad);
          imgElement.addEventListener('error', handleError);

          observer.unobserve(imgElement);
        }
      });
    };

    // Create an Intersection Observer to lazy load the image
    observerRef.current = new IntersectionObserver(observerCallback, {
      rootMargin,
      threshold,
    });

    observerRef.current.observe(imgElement);

    return () => {
      if (observerRef.current && imgRef.current) {
        observerRef.current.unobserve(imgRef.current);
      }
      observerRef.current?.disconnect();
      imgElement.removeEventListener('load', handleLoad);
      imgElement.removeEventListener('error', handleError);
    };
  }, [rootMargin, threshold, fallback]);

  return (
    <div className={cn('overflow-hidden relative', wrapperClassName)}>
      {/* Placeholder */}
      {withPlaceholder ? (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-opacity duration-500',
            isLoaded ? 'opacity-0' : 'opacity-100',
          )}
        >
          {placeholder ? (
            <img
              src={placeholder}
              alt='placeholder'
              loading='lazy'
              className='w-full h-full object-cover'
            />
          ) : (
            <div
              className='w-full h-full bg-secondary-hover animate-pulse'
              aria-label='Loading image'
            />
          )}
        </div>
      ) : null}

      {/* Main Image */}
      <img
        ref={imgRef}
        data-src={src}
        alt={alt}
        loading='lazy'
        className={cn(
          'transition-all duration-500 ease-in-out w-full h-full object-cover',
          isLoaded ? 'opacity-100' : 'opacity-0',
          className,
        )}
        {...props}
      />
    </div>
  );
}
